import { gql } from '@apollo/client'
import { DTVFragment } from '../fragments/dtv.fragments'
import { ElementMetadataFragment } from '../fragments/elementMetadata.fragments'
import { ProjectItemFragments } from '../fragments/projectItem.fragments'
import {
    tableInfoFragment,
    tableListFragment,
} from '../fragments/table.fragment'

export const GetProjectElementNodesQuery = gql`
    query getProjectElementNodes($input: GetProjectElementNodesInput!) {
        getProjectElementNodes(input: $input) {
            projectElementNodes {
                ...projectElementNodes
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetProjectElementNodesForAEProjectWSQuery = gql`
    query getProjectElementNodesForAEProjectWorkspace(
        $input: GetProjectElementNodesInput!
    ) {
        getProjectElementNodes(input: $input) {
            projectElementNodes {
                ...projectElementNodes
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetProjectElementNodesWithMetadataForAEProjectWSQuery = gql`
    query getProjectElementNodesForAEProjectWorkspace(
        $input: GetProjectElementNodesInput!
    ) {
        getProjectElementNodes(input: $input) {
            projectElementNodes {
                ...projectElementNodesWithMetadata
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodesWithMetadata}
`

export const CheckHasPublishedIterationsQuery = gql`
    query getProjectElementNodesForAEProjectWorkspace(
        $input: GetProjectElementNodesInput!
        $projectId: String!
    ) {
        getProjectElementNodes(input: $input) {
            projectElementNodes {
                ...projectElementNodes
                hasPublishedIterations(projectId: $projectId)
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetElementNodesForOfficeMasterWorkspaceQuery = gql`
    query getElementNodesForOfficeMasterWorkspace(
        $input: GetOMProjectElementNodesInput!
    ) {
        getElementNodesForOfficeMasterWorkspace(input: $input) {
            projectElementNodes {
                ...projectElementNodes
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetDropdownElementNodesForOfficeMasterWorkspaceQuery = gql`
    query getFamilyIterationWithElementTreeNodesForOfficeMasterWorkspace(
        $input: GetOMFamilyIterationAndElementNodesInput!
    ) {
        getFamilyIterationWithElementTreeNodesForOfficeMasterWorkspace(
            input: $input
        ) {
            familyIterations {
                familyNumber
                iterationName
                elementNodes {
                    ...projectElementNodes
                }
            }
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetElementNodesForOfficeMasterElementList = gql`
    query GetElementNodesForOfficeMasterElementList(
        $input: GetOMProjectElementNodesInput!
    ) {
        getElementNodesForOfficeMasterElementList(input: $input) {
            projectElementNodes {
                ...projectElementNodes
                iteration {
                    id
                    name
                }
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetFamilyIterationElementNodesForOfficeMaster = gql`
    query GetFamilyIterationElementNodesForOfficeMaster(
        $input: GetOMFamilyIterationElementNodesInput!
    ) {
        getFamilyIterationElementNodesForOfficeMaster(input: $input) {
            projectElementNodes {
                ...projectElementNodes
                iteration {
                    id
                    name
                }
                metadata {
                    created
                    createdBy {
                        userFullName
                    }
                    modifiedBy {
                        userFullName
                    }
                    updated
                }
                attachedToFirmId
                isBpPublished
                isPrimaryIteration
                isMasterSpecClone
            }
            totalCount
        }
    }
    ${ProjectItemFragments.ProjectElementNodes}
`

export const GetOfficeMasterProductFamilyIterationsQuery = gql`
    query getOfficeMasterProductFamilyIterations(
        $input: GetProductFamilyIterationsInput!
    ) {
        getOfficeMasterProductFamilyIterations(input: $input) {
            productFamilies {
                familyId
                id
                documentType
                publishedByDocumentType
                isOMDraftAvailable
                iteration {
                    id
                    name
                }
            }
            count
        }
    }
`

export const GetFamilyNodesBySustainabilityTopicQuery = gql`
    query GetFamilyNodesBySustainabilityTopic(
        $input: FamilyNodesBySustainabilityTopicInput!
    ) {
        getFamilyNodesBySustainabilityTopic(input: $input) {
            projectElementNodes {
                id
                parentNodeId
                text
                elementType
                treePath
                elementId
                projectId
                baseElementId
            }
            totalCount
        }
    }
`

export const SearchElementNodesForOfficeMasterElementList = gql`
    query SearchElementNodesForOfficeMasterElementList(
        $input: SearchOfficeMasterElementNodesInput!
    ) {
        searchElementNodesForOfficeMasterElementList(input: $input) {
            projectElementNodes {
                id
                elementId
                baseElementId
                nodeOrder
                isInProject
                isDeactivated
                elementType
                text
                treePath
                parentTreePath
                parentNodeId
                projectId
                elementId
                isSearchResult
                hasChildren
                isLastSubAssembly
                isImportedToProject
                importedToProjectId
                isBasedFromImportedNode
                isBestPractice
                isGlobalBestPractice
                hasBestPractice
                isBasedFromManufacturerOption
                isProjectOnly
                isBpPublished
                attachedToFirmId
                isBpPublished
            }
            totalCount
        }
    }
`

export const GetProjectUniformatClassificationElement = gql`
    query getProjectUniformatClassificationElement(
        $input: GetProjectElementInput!
    ) {
        getProjectUniformatClassificationElement(input: $input) {
            id
            dataType
            name
            projectElementMetadata {
                assignedUser
                assignedUserId
                baseElementId
                currentLOD
                projectId
                targetLOD
                state
            }
        }
    }
`
export const GetProjectUniformatClassification = gql`
    query getProjectUniformatClassification($input: GetProjectElementInput!) {
        getProjectUniformatClassification(input: $input) {
            projectElementMetadata {
                assignedUser
                assignedUserId
                baseElementId
                currentLOD
                projectId
                state
                targetLOD
            }
            uniformatClassificationProperties {
                basePropertyId
                hint
                id
                firmId
                name
                units
                value
            }
            notesCount
        }
    }
`

export const GetUniformatClassification = gql`
    query getUniformatClassification($input: GetUniformatClassificationInput!) {
        getUniformatClassification(input: $input) {
            id
            code
            uniformatClassificationProperties {
                basePropertyId
                hint
                firmId
                id
                name
                value
                units
            }
        }
    }
`

export const getChildElementQuery = (depth: number): string => {
    if (depth === 0) return ''
    return `
    elements {
        id
        isOptionItem
        imperialUnits
        metricUnits
        optionItemState
        fieldId
        text
        type        
        placeholderText
        isPremiumPlus
        isBestPractice
        isGlobalBestPractice
        changeType
        authoredUserId
        ${getChildElementQuery(depth - 1)}
    }
    `
}

export const getChildElementQueryForSetProjectContentItemElements = (
    depth: number
): string => {
    if (depth === 0) return ''
    return `
    elements {
        id
        isOptionItem
        imperialUnits
        metricUnits
        optionItemState
        fieldId
        text
        type        
        placeholderText
        isPremiumPlus
        isBestPractice
        isGlobalBestPractice
        changeType
        authoredUserId
        ${getChildElementQuery(depth - 1)}
    }
    `
}

export const ProductFamilyQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query productFamily($id: String!, $projectId: String!, $partType: TemplateItemPartType!, $rootArticleContentItemIds: [String]){
            productFamily(id: $id) 
            {
                id
                prefix
                familyId
                delimiter
                name
                tableList {
                    ...tableListFragment
                  }
                contentItemNodes (input: {
                    projectId: $projectId,
                    templateItemPartType: $partType,
                    rootArticleContentItemId: $rootArticleContentItemIds
                }) {
                    id
                    contentType
                    productFamilyContentId
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
            }
        }
        ${tableListFragment}
    `
}

export const ProductFamilyViewQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)

    return gql`
        query productFamilyView($id: String!, $projectId: String!, $partType: TemplateItemPartType!, $rootArticleContentItemIds: [String], $nodeId: String, $isFromSMM: Boolean) {
            productFamilyView(id: $id) {
                id
                prefix
                familyId
                delimiter
                name
                tableList {
                    ...tableListFragment
                    __typename
                }
                contentItemViewNodes(
                    input: {projectId: $projectId, templateItemPartType: $partType, rootArticleContentItemId: $rootArticleContentItemIds, nodeId: $nodeId, isFromSMM: $isFromSMM}
                ) {
                    id
                    contentType
                    productFamilyContentId
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                    partType
                    type
                    dtvContentItemParentId
                    levelOfSpecificationTag
                    order
                    dtvParentId
                    pTDesignationKey
                    dtvItemId
                    order
                    isHidden
                    isDeactivated
                    isUserAdded
                    propertyRefId
                    paragraphOrder
                    isSustainabilityContentItem
                    isForProductType
                    isProductType
                    isManufacturers
                    isBOD
                    isSharedProductTypeProperty
                    isProductGroupHasMultipleProductTypes
                    isProductGroup
                    isDeleted
                    isTable
                    activatedManually
                    isProductTypeNested
                    isAddedToProjectProductType
                    isCreatedFromPremiumPlus
                    isSharedProperty
                    isSelectedSustainabilityContentItem
                    pillId
                    refBodItemId
                    isPillSelected
                    isImported
                    documentTemplateViewId
                    isCustomManufacturer
                    aeCustomManufacturerOrder
                    drawingDesignation
                    __typename
                }
                __typename
            }
        }
        ${tableListFragment}
    `
}

export const ProductFamilyDocumentTemplateViewQuery = gql`
    query productFamilyDocumentTemplateView(
        $id: String!
        $partType: TemplateItemPartType!
    ) {
        productFamily(id: $id) {
            documentTemplateView(partType: $partType) {
                ...dtvFragment
            }
        }
    }
    ${DTVFragment}
`
export const GetProjectProductFamilyElementMetadata = gql`
    query getProjectProductFamilyElementMetada(
        $input: GetProjectElementInput!
    ) {
        getProjectProductFamily(input: $input) {
            projectElementMetadata {
                assignedUser
                assignedUserId
                baseElementId
                currentLOD
                projectId
                state
                targetLOD
                isTargetLevelOfSpecificationManuallyUpdated
            }
        }
    }
`

export const GetProjectProductFamilyQueryWithTable = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectProductFamilyWithTable($elementId: String!, $projectId: String!, $partType: TemplateItemPartType, $rootArticleContentItemIds: [String],$familyNodeId:String!){
            getProjectProductFamily(input:{
                elementId: $elementId,
                projectId: $projectId
            }) 
            {
                id
                
                tableList {
                    ...tableListFragment
                  }
                previewSection(previewSectionInput:{
                    familyNodeId: $familyNodeId,
                    projectId: $projectId
                }) {
                    tables {
                        ...tableInfoFragment
                      }
                    
                }
                contentType
                prefix
                familyId
                delimiter
                name
                hasProductType
                notesCount
                contentItemNodes (input: {
                    projectId: $projectId,
                    templateItemPartType: $partType,
                    rootArticleContentItemId: $rootArticleContentItemIds
                }) {
                    id
                    contentType
                    productFamilyContentId
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
                documentTemplateView(
                    partType: $partType
                    
                ) {
                    ...dtvFragment
                }
                projectNuggets{
                    id
                    projectProductTypeProperties{
                        id
                        name
                        projectElementMetadata {
                            baseElementId
                        }
                        options {
                            id
                            value
                        }
                        optionGroups{
                            options {
                                id
                                isFillInTheBlank
                                isPremium
                                isPremiumPlus
                                value
                            }
                        }
                        value {
                            contentItems{
                                id
                            }
                        }
                    }
                }
            }
        }
        ${DTVFragment}
        ${tableInfoFragment}
        ${tableListFragment}
    `
}

export const GetProjectProductFamilyViewQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)

    return gql`
        query getProjectProductFamilyView($elementId: String!, $projectId: String!,
        $partType: TemplateItemPartType!, $rootArticleContentItemIds: [String], $isTrackChangeToggleOn: Boolean!, $nodeId: String, $isFromSMM: Boolean, $features: [String]) {
            getProjectProductFamilyView(input: {elementId: $elementId, projectId: $projectId}) {
                id
                contentType
                prefix
                familyId
                delimiter
                name
                hasProductType
                notesCount
                tableList {
                    ...tableListFragment
                }
                contentItemViewNodes(
                    input: {projectId: $projectId, templateItemPartType: $partType, rootArticleContentItemId: $rootArticleContentItemIds, isTrackChangeToggleOn: $isTrackChangeToggleOn, nodeId: $nodeId, isFromSMM: $isFromSMM, features: $features}
                    ) {
                        id
                        documentTemplateViewId
                        productFamilyId
                        projectId
                        contentType
                        productFamilyContentId
                        comments
                        parentComments
                        textType
                        isProductGroup
                        isBestPractice
                        isGlobalBestPractice
                        ${elements}
                        partType
                        type
                        dtvParentId
                        levelOfSpecificationTag
                        order
                        pTDesignationKey
                        dtvItemId
                        order
                        isHidden
                        isDeactivated
                        isUserAdded
                        propertyRefId
                        paragraphOrder
                        isSustainabilityContentItem
                        isSelectedSustainabilityContentItem
                        isForProductType
                        isProductType
                        isManufacturers
                        isBOD
                        isSharedProductTypeProperty
                        isProductGroupHasMultipleProductTypes
                        dtvContentItemParentId
                        isSharedProperty
                        isAddedToProject
                        pillId
                        refBodItemId
                        isPillSelected
                        isImported
                        documentTemplateViewId
                        isCustomManufacturer
                        aeCustomManufacturerOrder
                        isAddedInOfficeMasterDraftProject
                        isCreatedFromPremiumPlus
                        drawingDesignation
                        __typename
                    }
                __typename
                metadata {
                    ...elementMetadata
                }
                projectElementMetadata {
                    assignedUser
                    assignedUserId
                    baseElementId
                    currentLOD
                    projectId
                    targetLOD
                    state
                    isTargetLevelOfSpecificationManuallyUpdated
                }
            }
        }
            ${ElementMetadataFragment}
            ${tableListFragment}
    `
}

export const GetFamilyAddedProductTypesQuery = gql`
    query getProjectProductFamily(
        $elementId: String!
        $projectId: String!
        $familyNodeId: String!
    ) {
        getProjectProductFamily(
            input: { elementId: $elementId, projectId: $projectId }
        ) {
            familyAddedProductTypes(familyNodeId: $familyNodeId) {
                id
                elementId
                baseElementId
                parentNodeId
            }
        }
    }
`

export const GetProjectProductFamilyQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectProductFamily($elementId: String!, $projectId: String!, $partType: TemplateItemPartType, $rootArticleContentItemIds: [String], $familyNodeId: String){
            getProjectProductFamily(input:{
                elementId: $elementId,
                projectId: $projectId
            }) 
            {
                id
                contentType
                prefix
                familyId
                delimiter
                name
                hasProductType
                notesCount
                contentItemNodes (input: {
                    projectId: $projectId,
                    templateItemPartType: $partType,
                    rootArticleContentItemId: $rootArticleContentItemIds
                }) {
                    id
                    contentType
                    productFamilyContentId
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
                documentTemplateView(
                    partType: $partType
                    
                ) {
                    ...dtvFragment
                }
                projectNuggets{
                    id
                    projectProductTypeProperties{
                        id
                        name
                        projectElementMetadata {
                            baseElementId
                        }
                        options {
                            id
                            value
                        }
                        optionGroups{
                            options {
                                id
                                isFillInTheBlank
                                isPremium
                                isPremiumPlus
                                value
                            }
                        }
                        value {
                            contentItems{
                                id
                            }
                        }
                    }
                }
                familyAddedProductTypes(
                    familyNodeId: $familyNodeId
                ) {
                    id
                    elementId
                    baseElementId
                    treePath
                }
                familyProductListings(
                    familyNodeId: $familyNodeId
                ) {
                    firmName
                    isBOD
                    isSelected
                    listingId
                    listingText
                    order
                    productName
                    type
                    parentId
                    isBestPractice
                    isGlobalBestPractice
                }
                familyCustomManufacturerProducts (
                    familyNodeId: $familyNodeId
                ) {
                    listingText
                    isBOD
                    customManufacturerProductId
                    parentId
                    isAddedInOfficeMasterDraftProject
                    aeCustomManufacturerOrder
                }
                familyManufacturerImportedManufacturer(familyNodeId: $familyNodeId) {
                    isBOD
                    isSelected
                    manufacturerContentItemId
                    manufacturerName
                    parentNodeId
                    isBestPractice
                    isGlobalBestPractice
                }
                familyBodImportedManufacturer(familyNodeId: $familyNodeId) {
                    isBOD
                    isSelected
                    manufacturerContentItemId
                    manufacturerName
                    parentNodeId
                }
            }
        }
        ${DTVFragment}
    `
}

export const GetProjectProductFamilyContentItemNodes = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectProductFamilyContentItemNodes($elementId: String!, $projectId: String!, $partType: TemplateItemPartType, $rootArticleContentItemIds: [String]){
            getProjectProductFamily(input:{
                elementId: $elementId,
                projectId: $projectId
            }) 
            {
                id
                familyId
                contentItemNodes (input: {
                    projectId: $projectId,
                    templateItemPartType: $partType,
                    rootArticleContentItemId: $rootArticleContentItemIds
                }) {
                    id
                    contentType
                    productFamilyContentId
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
            }
        }
    `
}

export const GetUpdatedProjectProductFamilyProductListingQuery = () => {
    return gql`
        query getProjectProductFamilyProductListingQuery(
            $elementId: String!
            $projectId: String!
            $familyNodeId: String
        ) {
            getProjectProductFamily(
                input: { elementId: $elementId, projectId: $projectId }
            ) {
                familyProductListings(familyNodeId: $familyNodeId) {
                    firmName
                    isBOD
                    isSelected
                    listingId
                    listingText
                    order
                    productName
                    type
                    parentId
                    isBestPractice
                    isGlobalBestPractice
                }
                familyCustomManufacturerProducts(familyNodeId: $familyNodeId) {
                    listingText
                    isBOD
                    customManufacturerProductId
                    parentId
                    isAddedInOfficeMasterDraftProject
                    aeCustomManufacturerOrder
                }
                familyManufacturerImportedManufacturer(
                    familyNodeId: $familyNodeId
                ) {
                    isBOD
                    isSelected
                    manufacturerContentItemId
                    manufacturerName
                    parentNodeId
                    isBestPractice
                    isGlobalBestPractice
                }
                familyBodImportedManufacturer(familyNodeId: $familyNodeId) {
                    isBOD
                    isSelected
                    manufacturerContentItemId
                    manufacturerName
                    parentNodeId
                }
            }
        }
    `
}

export const ProjectProductFamilyDocumentTemplateViewQuery = gql`
    query getProjectProductFamilyDocumentTemplateView(
        $input: GetProjectElementInput!
        $partType: TemplateItemPartType
        $isTrackChangeToggleOn: Boolean
    ) {
        getProjectProductFamily(input: $input) {
            documentTemplateView(
                partType: $partType
                isTrackChangeToggleOn: $isTrackChangeToggleOn
            ) {
                ...dtvFragment
            }
        }
    }
    ${DTVFragment}
`

export const NuggetQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query nugget($id: String!, $projectId: String!, $productFamilyId: String!, $partType: TemplateItemPartType!, $rootArticleContentItemIds: [String]){
            nugget(id:$id) {
                id
                specAgentId
                contentItemNodes (input: {
                    projectId: $projectId,
                    productFamilyId: $productFamilyId,
                    rootArticleContentItemId: $rootArticleContentItemIds,
                    templateItemPartType: $partType,
                }) {
                    id
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
            }
        }
    `
}

export const NuggetContentViewQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)

    return gql`
        query nuggetContentView(
            $id: String!, 
            $projectId: String!, 
            $partType: TemplateItemPartType!, 
            $rootArticleContentItemIds: [String], 
            $nodeId: String!, 
            $isTrackChangeToggleOn: Boolean,
            $isFromSMM: Boolean
        ) 
        {
        nugget(id: $id) {
            id
            contentItemViewNodes(
                input: {
                    projectId: $projectId, 
                    templateItemPartType: $partType, 
                    rootArticleContentItemId: $rootArticleContentItemIds, 
                    isTrackChangeToggleOn: $isTrackChangeToggleOn, 
                    nodeId: $nodeId,
                    isFromSMM: $isFromSMM
                }
            ) {
                id
                contentType
                productFamilyContentId
                comments
                parentComments
                textType
                isProductGroup
                isBestPractice
                isGlobalBestPractice
                ${elements}
                partType
                type
                dtvParentId
                levelOfSpecificationTag
                order
                dtvItemId
                pTDesignationKey
                order
                isHidden
                isDeactivated
                isUserAdded
                propertyRefId
                paragraphOrder
                isSustainabilityContentItem
                isForProductType
                isProductType
                isManufacturers
                isBOD
                isSharedProductTypeProperty
                isProductGroupHasMultipleProductTypes
                dtvContentItemParentId
                pillId
                refBodItemId
                isPillSelected
                isImported
                isCustomManufacturer
                aeCustomManufacturerOrder
                isAddedInOfficeMasterDraftProject
                isCreatedFromPremiumPlus
                drawingDesignation
                __typename
            }
            __typename
        }
    }
    `
}

export const GetProductTypeProductListingsInfo = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProductTypeProductListingsInfo($id: String!, $nodeId: String!) {
            nugget(id: $id) {
                id
                bodImportedManufacturer(nodeId: $nodeId) {
                    isBOD
                    isSelected
                    manufacturerContentItemId
                    manufacturerName
                }
                manufacturerImportedManufacturer(nodeId: $nodeId) {
                    isBOD
                    isSelected
                    manufacturerContentItemId
                    manufacturerName
                    isBestPractice
                    isGlobalBestPractice
                }
                productTypeProperties(nodeId: $nodeId) {
                    id
                    name
                    options {
                        id
                        isFillInTheBlank
                        isPremium
                        isPremiumPlus
                        value
                    }
                    value {
                        contentItems {
                            id
                            comments
                            textType
                            ${elements}
                        }
                    }
                }
                productGroups(nodeId: $nodeId
                    ){
                    id
                    allContent{
                        contentItems {
                            id
                            comments
                            textType
                            ${elements}
                        }
                    }
                }
                productListings(nodeId: $nodeId){
                    firmName
                    isBOD
                    isSelected
                    listingId
                    listingText
                    order
                    productName
                    type
                    isBestPractice
                    isGlobalBestPractice
                }
            }
        }
    `
}

export const NuggetProductTypePropertiesViewQuery = gql`
    query getNuggetProductTypePropertiesView(
        $id: String!
        $nodeId: String!
        $isTrackChangeToggleOn: Boolean
    ) {
        nugget(id: $id) {
            id
            productTypePropertiesView(
                nodeId: $nodeId
                isTrackChangeToggleOn: $isTrackChangeToggleOn
            ) {
                ...dtvFragment
            }
        }
    }
    ${DTVFragment}
`

export const GetProjectNuggetQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectNugget(
            $elementId: String!,
            $projectId: String!,
            $productFamilyId: String!,
            $partType: TemplateItemPartType!,
            $rootArticleContentItemIds: [String]) {
            getProjectNugget(input:{
                elementId: $elementId,
                projectId: $projectId,
            }) {
                id
                name
                specAgentId
                isFromImportedNugget
                notesCount
                contentItemNodes (input: {
                    projectId: $projectId,
                    productFamilyId: $productFamilyId,
                    rootArticleContentItemId: $rootArticleContentItemIds,
                    templateItemPartType: $partType,
                }) {
                    id
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
                projectElementMetadata{
                    currentLOD
                    targetLOD
                    state
                    assignedUser
                    assignedUserId
                    isTargetLevelOfSpecificationManuallyUpdated
                }
                drawingDesignation
            }
        }
    `
}

export const GetProjectNuggetContentViewQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectNuggetContentView(
            $elementId: String!,
            $projectId: String!,
            $partType: TemplateItemPartType!,
            $rootArticleContentItemIds: [String],
            $nodeId: String,
            $isTrackChangeToggleOn: Boolean,
            $isFromSMM: Boolean) {
            getProjectNugget(input:{
                elementId: $elementId,
                projectId: $projectId,
            }) {
                id
                name
                specAgentId
                isFromImportedNugget
                notesCount
                contentItemViewNodes(
                    input: {
                        projectId: $projectId,
                        templateItemPartType: $partType,
                        rootArticleContentItemId: $rootArticleContentItemIds,
                        isTrackChangeToggleOn: $isTrackChangeToggleOn,
                        nodeId: $nodeId,
                        isFromSMM: $isFromSMM
                    }
                ) {
                    id
                    contentType
                    productFamilyId
                    productFamilyContentId
                    comments
                    contentItemOwnerType
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    isAddedInOfficeMasterDraftProject
                    ${elements}
                    partType
                    type
                    dtvParentId
                    levelOfSpecificationTag
                    order
                    dtvItemId
                    order
                    isHidden
                    isDeactivated
                    isUserAdded
                    propertyRefId
                    paragraphOrder
                    isSustainabilityContentItem
                    isForProductType
                    isProductType
                    isManufacturers
                    isBOD
                    isSharedProductTypeProperty
                    isProductGroupHasMultipleProductTypes
                    dtvContentItemParentId
                    pillId
                    refBodItemId
                    isPillSelected
                    isImported
                    documentTemplateViewId
                    productFamilyId
                    productFamilyContentId
                    projectId
                    isCustomManufacturer
                    aeCustomManufacturerOrder
                    isCreatedFromPremiumPlus
                    drawingDesignation
                    pTDesignationKey
                    __typename
                }
                projectElementMetadata{
                    assignedUser
                    assignedUserId
                    baseElementId
                    currentLOD
                    projectId
                    targetLOD
                    state
                    isTargetLevelOfSpecificationManuallyUpdated
                }
                drawingDesignation
                metadata {
                    ...elementMetadata
                }
            }
        }
        ${ElementMetadataFragment}
    `
}

export const GetProjectNuggetContentItemNodes = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectNugget(
            $elementId: String!,
            $projectId: String!,
            $productFamilyId: String!,
            $partType: TemplateItemPartType!,
            $rootArticleContentItemIds: [String]) {
            getProjectNugget(input:{
                elementId: $elementId,
                projectId: $projectId,
            }) {
                id
                name
                specAgentId
                isFromImportedNugget
                notesCount
                contentItemNodes (input: {
                    projectId: $projectId,
                    productFamilyId: $productFamilyId,
                    rootArticleContentItemId: $rootArticleContentItemIds,
                    templateItemPartType: $partType,
                }) {
                    id
                    comments
                    parentComments
                    textType
                    isProductGroup
                    isBestPractice
                    isGlobalBestPractice
                    ${elements}
                }
                drawingDesignation
            }
        }
    `
}

export const GetProjectProductTypeProductListingsInfo = gql`
    query getProjectProductTypeProductListingsInfo(
        $input: GetProjectElementInput!
        $nodeId: String!
        $isTrackChangeToggleOn: Boolean
    ) {
        getProjectNugget(input: $input) {
            id
            specAgentId
            bodImportedManufacturer(nodeId: $nodeId) {
                isBOD
                isSelected
                manufacturerContentItemId
                manufacturerName
            }
            manufacturerImportedManufacturer(nodeId: $nodeId) {
                isBOD
                isDeactivated
                isSelected
                manufacturerContentItemId
                manufacturerName
                isBestPractice
                isGlobalBestPractice
            }
            isFromImportedNugget
            customManufacturerProducts(nodeId: $nodeId) {
                listingText
                isBOD
                customManufacturerProductId
                isAddedInOfficeMasterDraftProject
                aeCustomManufacturerOrder
            }
            projectProductTypePropertiesView(
                nodeId: $nodeId
                isTrackChangeToggleOn: $isTrackChangeToggleOn
            ) {
                productFamilyId
                projectId
            }
            projectProductTypeProperties {
                id
                name
                projectElementMetadata {
                    baseElementId
                }
                value {
                    contentItems {
                        id
                    }
                }
                optionGroups {
                    id
                    options {
                        id
                        value
                        isPremiumPlus
                    }
                }
                options {
                    id
                    value
                }
            }
            productListings(nodeId: $nodeId) {
                ...productListingFragment
            }
        }
    }
    ${ProjectItemFragments.ProductListing}
`
export const GetProjectProductTypeProductListingsInfoForBOD = (
    depth: number
) => {
    const elements = getChildElementQuery(depth)
    return gql`
        query getProjectProductTypeProductListingsInfo($input: GetProjectElementInput!) {
            getProjectNugget(input: $input) {
                isFromImportedNugget
                projectProductTypeProperties {
                    id
                    name
                    projectElementMetadata {
                        baseElementId
                    }
                    value {
                        contentItems {
                            id
                            comments
                            textType
                            ${elements}
                        }
                    }
                    optionGroups{
                        id
                        options {
                            id
                            value
                            isPremiumPlus
                        }
                    }
                    options {
                        id
                        value
                    }
                }
            }
        }
    `
}
export const ProjectNuggetProductTypePropertiesViewQuery = gql`
    query getProjectNuggetProductTypePropertiesView(
        $input: GetProjectElementInput!
        $nodeId: String!
        $isTrackChangeToggleOn: Boolean
    ) {
        getProjectNugget(input: $input) {
            id
            isFromImportedNugget
            drawingDesignation
            customManufacturerProducts(nodeId: $nodeId) {
                listingText
                isBOD
                customManufacturerProductId
                isAddedInOfficeMasterDraftProject
                aeCustomManufacturerOrder
            }
            projectElementMetadata {
                baseElementId
            }
            projectProductTypePropertiesView(
                nodeId: $nodeId
                isTrackChangeToggleOn: $isTrackChangeToggleOn
            ) {
                ...dtvFragment
            }
        }
    }
    ${DTVFragment}
`

export const GetProjectManufacturerProductListingQuery = gql`
    query getProjectManufacturerProductListing(
        $input: GetProjectManufacturerProductListingInput!
    ) {
        getProjectManufacturerProductListing(input: $input) {
            listingId
            elementId
            isBOD
            type
            firmName
            productType
            hasSupportedManufacturersOptions
        }
    }
`
export const GetProjectNuggetForProductListingsQuery = (depth: number) => {
    const elements = getChildElementQuery(depth)
    return gql`
    query getProjectNuggetForProductListingsQuery(
        $input: GetProjectElementInput!
        $nodeId: String!
    ) {
        getProjectNugget(input: $input) {
            id
            bodImportedManufacturer(nodeId: $nodeId) {
                isBOD
                isSelected
                manufacturerContentItemId
                manufacturerName
            }
            manufacturerImportedManufacturer(nodeId: $nodeId) {
                isBOD
                isSelected
                manufacturerContentItemId
                manufacturerName
                isBestPractice
                isGlobalBestPractice
            }
            isFromImportedNugget
            projectProductTypeProperties {
                id
                name
                projectElementMetadata {
                    baseElementId
                }
                value {
                    contentItems {
                        id
                        comments
                        textType
                        ${elements}
                    }
                }
                optionGroups{
                    options {
                        id
                        value
                        isPremiumPlus
                    }
                }
                options {
                    id
                    value
                }
            }
            projectProductTypePropertiesView(nodeId: $nodeId) {
                productFamilyId
                projectId
            }
            customManufacturerProducts(nodeId: $nodeId) {
                listingText
                isBOD
                customManufacturerProductId
                isAddedInOfficeMasterDraftProject
                isSelected
                aeCustomManufacturerOrder
            }
            productListings(nodeId: $nodeId) {
                ...productListingFragment
            }
        }
    }
    ${ProjectItemFragments.ProductListing}
`
}

export const GetNuggetForProductListingsQuery = gql`
    query getNuggetForProductListingsQuery($id: String!, $nodeId: String!) {
        nugget(id: $id) {
            id
            productTypeProperties {
                name
            }
            productTypePropertiesView(nodeId: $nodeId) {
                productFamilyId
                projectId
            }
            productListings(nodeId: $nodeId) {
                isBOD
                listingId
            }
        }
    }
`

export const GetProductTypesWithBODQuery = gql`
    query getProductTypesWithBod(
        $nodeIds: [String]
        $input: ProductListingDivisionViewInput
    ) {
        getProductTypesWithBod(nodeIds: $nodeIds, input: $input) {
            productTypeName
            specAgentProductTypeId
        }
    }
`

export const GetProjectProductListings = gql`
    query getProjectProductListings(
        $input: GetProjectElementInput!
        $nodeId: String!
    ) {
        getProjectNugget(input: $input) {
            id
            isFromImportedNugget
            customManufacturerProducts(nodeId: $nodeId) {
                listingText
                isBOD
                customManufacturerProductId
                isAddedInOfficeMasterDraftProject
                aeCustomManufacturerOrder
            }
            productListings(nodeId: $nodeId) {
                firmName
                isBOD
                isSelected
                listingId
                listingText
                order
                productName
                type
                isBestPractice
                isGlobalBestPractice
            }
        }
    }
`

export const GetProjectProductTypeProperties = gql`
    query getProjectProductTypeProductListingsInfo(
        $input: GetProjectElementInput!
    ) {
        getProjectNugget(input: $input) {
            id
            isFromImportedNugget
            projectProductTypeProperties {
                id
                name
                projectElementMetadata {
                    baseElementId
                }
                value {
                    contentItems {
                        id
                    }
                }
                optionGroups {
                    id
                    options {
                        id
                        value
                        isPremiumPlus
                    }
                }
                options {
                    id
                    value
                }
            }
        }
    }
`

export const GetProductFamilyIterationQuery = gql`
    query getProductFamilyIterations($input: GetProductFamilyIterationsInput!) {
        getProductFamilyIterations(input: $input) {
            familyIterations {
                familyBaseElementId
                isSelected
                iterationId
                iterationName
            }
        }
    }
`

export const GetVersionHasTrackChangeQuery = gql`
    query hasTrackChanges($input: HasTrackChangeInput!) {
        hasTrackChanges(input: $input)
    }
`
